import {
  PolicyRecommendation,
  PolicyRecommendationStatus,
  BehavioralDomainId,
  StrictnessTier,
} from './target-types';
import { EncryptedTargetSyncManager } from './encrypted-target-sync';

export interface PolicyRecommendationRepository {
  getRecommendations(userId: string): Promise<PolicyRecommendation[]>;
  saveRecommendation(rec: PolicyRecommendation): Promise<void>;
  subscribeRecommendations(
    userId: string,
    onUpdate: (recs: PolicyRecommendation[]) => void
  ): () => void;
}

export class InMemoryPolicyRecommendationRepository
  implements PolicyRecommendationRepository {
  private store = new Map<string, Map<string, PolicyRecommendation>>();
  private listeners = new Map<
    string,
    Set<(recs: PolicyRecommendation[]) => void>
  >();

  async getRecommendations(userId: string): Promise<PolicyRecommendation[]> {
    const userMap = this.store.get(userId);
    if (!userMap) return [];
    return Array.from(userMap.values());
  }

  async saveRecommendation(rec: PolicyRecommendation): Promise<void> {
    if (!this.store.has(rec.userId)) {
      this.store.set(rec.userId, new Map());
    }
    this.store.get(rec.userId)!.set(rec.id, JSON.parse(JSON.stringify(rec)));

    const listeners = this.listeners.get(rec.userId);
    if (listeners) {
      const all = Array.from(this.store.get(rec.userId)!.values());
      listeners.forEach((fn) => fn(JSON.parse(JSON.stringify(all))));
    }
  }

  subscribeRecommendations(
    userId: string,
    onUpdate: (recs: PolicyRecommendation[]) => void
  ): () => void {
    if (!this.listeners.has(userId)) {
      this.listeners.set(userId, new Set());
    }
    const set = this.listeners.get(userId)!;
    set.add(onUpdate);

    // Immediate invoke
    const userMap = this.store.get(userId);
    onUpdate(userMap ? Array.from(userMap.values()) : []);

    return () => {
      set.delete(onUpdate);
    };
  }
}

export class PolicyRecommendationService {
  private repo: PolicyRecommendationRepository;

  constructor(repository?: PolicyRecommendationRepository) {
    this.repo = repository || new InMemoryPolicyRecommendationRepository();
  }

  async proposeRecommendation(params: {
    userId: string;
    proposerUid: string;
    proposerName: string;
    proposerRole?: 'clinician' | 'family_guardian';
    targetCategory: BehavioralDomainId;
    daytimeTier: StrictnessTier;
    nighttimeTier: StrictnessTier;
    nightStartHour?: number;
    nightEndHour?: number;
    allowOverride?: boolean;
    inactivityTimeoutMinutes?: number;
    continuousAllowanceMinutes?: number;
    customCbtPrompt?: string;
    recommendedTargets?: Array<{
      name: string;
      packageOrDomain: string;
      isCustom: boolean;
      enabled: boolean;
      inactivityTimeoutMinutes?: number;
      continuousAllowanceMinutes?: number;
    }>;
    rationale?: string;
  }): Promise<PolicyRecommendation> {
    const rec: PolicyRecommendation = {
      id: `rec_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      userId: params.userId,
      proposerUid: params.proposerUid,
      proposerName: params.proposerName,
      proposerRole: params.proposerRole || 'clinician',
      status: 'pending',
      targetCategory: params.targetCategory,
      daytimeTier: params.daytimeTier,
      nighttimeTier: params.nighttimeTier,
      nightStartHour: params.nightStartHour ?? 22,
      nightEndHour: params.nightEndHour ?? 6,
      allowOverride: params.allowOverride ?? false,
      inactivityTimeoutMinutes: params.inactivityTimeoutMinutes,
      continuousAllowanceMinutes: params.continuousAllowanceMinutes,
      customCbtPrompt: params.customCbtPrompt,
      recommendedTargets: params.recommendedTargets || [],
      rationale: params.rationale || 'Therapeutic policy adjustment recommended by your care team.',
      createdAt: new Date().toISOString(),
    };

    await this.repo.saveRecommendation(rec);
    return rec;
  }

  async getPendingRecommendations(userId: string): Promise<PolicyRecommendation[]> {
    const all = await this.repo.getRecommendations(userId);
    return all.filter((r) => r.status === 'pending');
  }

  async acceptRecommendation(
    userId: string,
    recId: string,
    syncManager: EncryptedTargetSyncManager
  ): Promise<{ success: boolean; recommendation?: PolicyRecommendation; error?: string }> {
    const all = await this.repo.getRecommendations(userId);
    const targetRec = all.find((r) => r.id === recId);
    if (!targetRec) {
      return { success: false, error: 'Recommendation not found.' };
    }

    if (targetRec.status !== 'pending') {
      return { success: false, error: `Recommendation is already ${targetRec.status}.` };
    }

    // 1. Update recommendation status
    targetRec.status = 'accepted';
    targetRec.respondedAt = new Date().toISOString();
    await this.repo.saveRecommendation(targetRec);

    // 2. Apply strictness rules to target document
    await syncManager.updateScheduledRule({
      categoryId: targetRec.targetCategory,
      daytimeTier: targetRec.daytimeTier,
      nighttimeTier: targetRec.nighttimeTier,
      nightStartHour: targetRec.nightStartHour,
      nightEndHour: targetRec.nightEndHour,
      allowOverride: targetRec.allowOverride,
      inactivityTimeoutMinutes: targetRec.inactivityTimeoutMinutes,
      continuousAllowanceMinutes: targetRec.continuousAllowanceMinutes,
    });

    // 3. Add or enable recommended targets if provided
    if (targetRec.recommendedTargets && targetRec.recommendedTargets.length > 0) {
      for (const recTarget of targetRec.recommendedTargets) {
        const added = await syncManager.addCustomTarget(
          targetRec.targetCategory,
          recTarget.packageOrDomain
        );
        if (
          added.target &&
          (recTarget.inactivityTimeoutMinutes !== undefined ||
            recTarget.continuousAllowanceMinutes !== undefined)
        ) {
          await syncManager.updateTargetLimits(added.target.id, {
            inactivityTimeoutMinutes: recTarget.inactivityTimeoutMinutes,
            continuousAllowanceMinutes: recTarget.continuousAllowanceMinutes,
          });
        }
      }
    }

    return { success: true, recommendation: targetRec };
  }

  async declineRecommendation(
    userId: string,
    recId: string
  ): Promise<{ success: boolean; recommendation?: PolicyRecommendation; error?: string }> {
    const all = await this.repo.getRecommendations(userId);
    const targetRec = all.find((r) => r.id === recId);
    if (!targetRec) {
      return { success: false, error: 'Recommendation not found.' };
    }

    targetRec.status = 'declined';
    targetRec.respondedAt = new Date().toISOString();
    await this.repo.saveRecommendation(targetRec);

    return { success: true, recommendation: targetRec };
  }

  subscribeToUserRecommendations(
    userId: string,
    onUpdate: (recs: PolicyRecommendation[]) => void
  ): () => void {
    return this.repo.subscribeRecommendations(userId, onUpdate);
  }
}
